/**
 * GLSL code for efficiently rendering a line grid. The grid line density is auto-adjusted based on camera distance.
 * @module
 */
import * as THREE from "three";

export const GridShader = {
  vertexShader: `varying vec3 vPos;
varying vec4 vScreenPos;

void main() {
  vPos = (modelMatrix * vec4( position, 1.0 )).xyz;
  gl_Position = projectionMatrix * modelViewMatrix * vec4( position, 1.0 );
  vScreenPos = gl_Position;
}`,

  fragmentShader: `
varying vec3 vPos;
varying vec4 vScreenPos;

uniform mat4 projectionMatrix;
uniform mat4 modelViewMatrix;

uniform float mediumGridInterval;
uniform float coarseGridInterval;

#if defined(OPAQUE)
uniform vec3 fadeOutColor;
#endif
uniform vec3 color;
uniform vec3 xAxisColor;
uniform vec3 yAxisColor;
uniform float alphaMult;
uniform bool fadeAxes;

vec2 pixelScreenSize;
vec2 screenPos;

vec2 toScreen(float x, float y, float z) {
  vec4 projected = (projectionMatrix * viewMatrix * vec4(x, y, z, 1.0));
  return projected.xy / projected.w;
}

// Round functions are not available in GLSL ES 1.00 - must be named something other than "round" to also keep compatibility with GLSL ES 3.00.
float roundCustom(float x) {
  return floor(x + 0.5);
}

vec2 roundCustom(vec2 x) {
  return floor(x + 0.5);
}

bool drawLine(vec2 linePointScreen, vec2 lineDirectionScreen) {
  if (abs(lineDirectionScreen.y) > abs(lineDirectionScreen.x)) {
    float a = (screenPos.y - linePointScreen.y) / lineDirectionScreen.y;
    float gridLineScreenXAtSameY = linePointScreen.x + a * lineDirectionScreen.x;

    if (abs(screenPos.x - gridLineScreenXAtSameY) <= pixelScreenSize.x * 0.5) {
      return true;
    }
  } else {
    float a = (screenPos.x - linePointScreen.x) / lineDirectionScreen.x;
    float gridLineScreenYAtSameX = linePointScreen.y + a * lineDirectionScreen.y;

    if (abs(screenPos.y - gridLineScreenYAtSameX) <= pixelScreenSize.y * 0.5) {
      return true;
    }
  }
  return false;
}

void main() {
  screenPos = vScreenPos.xy / vScreenPos.w;
  pixelScreenSize = vec2(dFdx(screenPos.x), dFdy(screenPos.y));

  vec2 deltaPerPixel = vec2(
    length(vec2(dFdx(vPos.x), dFdy(vPos.x))),
    length(vec2(dFdx(vPos.y), dFdy(vPos.y)))
    );

  vec2 gridInterval = vec2(1);
  if (deltaPerPixel.x > 0.2) {
    gridInterval.x = mediumGridInterval;
  }
  if (deltaPerPixel.y > 0.2) {
    gridInterval.y = mediumGridInterval;
  }
  if (deltaPerPixel.x > mediumGridInterval * 0.2) {
    gridInterval.x = coarseGridInterval;
  }
  if (deltaPerPixel.y > mediumGridInterval * 0.2) {
    gridInterval.y = coarseGridInterval;
  }

  float hitGridInterval = 1.0;

  vec2 nearestGridLine = roundCustom(vPos.xy / gridInterval) * gridInterval;

  vec2 gridXLinePointScreen = toScreen(nearestGridLine.x, vPos.y, vPos.z);
  vec2 gridXLineDirectionScreen = gridXLinePointScreen - toScreen(nearestGridLine.x, vPos.y - 100.0, vPos.z);

  // This line is vertical in world space (constant x)
  bool drawGridlineX = drawLine(gridXLinePointScreen, gridXLineDirectionScreen);

  vec2 gridYLinePointScreen = toScreen(vPos.x, nearestGridLine.y, vPos.z);
  vec2 gridYLineDirectionScreen = gridYLinePointScreen - toScreen(vPos.x - 100.0, nearestGridLine.y, vPos.z);

  // This line is horizontal in world space (constant y)
  bool drawGridlineY = drawLine(gridYLinePointScreen, gridYLineDirectionScreen);

  if (!drawGridlineX && !drawGridlineY) {
    discard;
  }

  vec2 nearestGridLinePerCoarseInterval = nearestGridLine / coarseGridInterval;
  vec2 nearestGridLinePerMediumInterval = nearestGridLine / mediumGridInterval;

  if (drawGridlineX) {
    if (abs(roundCustom(nearestGridLinePerCoarseInterval.x) - nearestGridLinePerCoarseInterval.x) < 0.01) {
      hitGridInterval = coarseGridInterval;
    } else if (abs(roundCustom(nearestGridLinePerMediumInterval.x) - nearestGridLinePerMediumInterval.x) < 0.01) {
      hitGridInterval = mediumGridInterval;
    }
  }
  if (drawGridlineY) {
    if (abs(roundCustom(nearestGridLinePerCoarseInterval.y) - nearestGridLinePerCoarseInterval.y) < 0.01) {
      hitGridInterval = max(hitGridInterval, coarseGridInterval);
    } else if (abs(roundCustom(nearestGridLinePerMediumInterval.y) - nearestGridLinePerMediumInterval.y) < 0.01) {
      hitGridInterval = max(hitGridInterval, mediumGridInterval);
    }
  }

  float fadeStrength = hitGridInterval / length(deltaPerPixel);

  float strength = clamp(fadeStrength * 0.02, 0.0, 1.0);
  if (hitGridInterval == coarseGridInterval) {
    // Boost the most prominent grid lines.
    strength = sqrt(strength);
  }

  vec3 chosenColor = color;
  float alpha = strength * alphaMult;

  // Special coloring for positive x and y axis controllable by uniforms.
  if (abs(nearestGridLine.x) <= deltaPerPixel.x * 0.5 && vPos.y >= 0.0 && drawGridlineX) {
    chosenColor = yAxisColor;
    if (!fadeAxes) {
      alpha = 1.0;
    }
  } else if (abs(nearestGridLine.y) <= deltaPerPixel.y * 0.5 && vPos.x >= 0.0 && drawGridlineY) {
    chosenColor = xAxisColor;
    if (!fadeAxes) {
      alpha = 1.0;
    }
  }

  #if defined(OPAQUE)
  gl_FragColor = vec4(mix(fadeOutColor, chosenColor, alpha), 1.0);
  #else
  gl_FragColor = vec4(chosenColor, alpha);
  #endif

  #include <colorspace_fragment>
}`,

  uniforms: {
    alphaMult: { value: 0.5 },
    fadeOutColor: { value: new THREE.Color(1, 1, 1) },
    color: { value: new THREE.Color(0.4, 0.4, 0.4) },
    xAxisColor: { value: new THREE.Color(1, 0, 0) },
    yAxisColor: { value: new THREE.Color(0, 1, 0) },
    fadeAxes: { value: false },
    mediumGridInterval: { value: 12.0 },
    coarseGridInterval: { value: 60.0 },
  },
} as const;
